import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Row } from '@sainsburys-tech/bolt'
import Image from '../../../helpers/Image/Image'
import * as Styled from './HotSpot.styles'
import AllProductsView from './AllProductsView'
import Recommendations from '../recommendations/Recommendations'
import { quickviewTracking } from './hot-spot.tracking'
import Editorial from '../editorial/Editorial'

const getProductId = (product, isEditorial) =>
  isEditorial ? product.target : product.target.substr(product.target.lastIndexOf('/') + 1)

const HotSpot = ({
  altText,
  image,
  imageFormat,
  aspect,
  width,
  quality,
  hotSpots,
  heading,
  pointOfInterest,
  'data-test': dataTest,
  isEditorial,
  items
}) => {
  const [selectedProductId, changeSelectedProductId] = useState(null)
  const [showProductsView, toggleProductsView] = useState(false)
  const [showLook, setShowLook] = useState(true)

  const shopAllText = 'Shop All Products'
  const iconSize = '0.94em'
  const imageTemplates = [
    '$poi$',
    `scaleFit=poi&poi=${pointOfInterest.x},${pointOfInterest.y},${pointOfInterest.w},${pointOfInterest.h}`,
    `&sm=aspect&aspect=${aspect}`
  ]

  const containsImage = items?.some((element) => element.image !== undefined)

  const allProducts = hotSpots.map((hotSpot) => getProductId(hotSpot, isEditorial))

  const handleHotSpotClick = (productId) => {
    const selectedProduct = selectedProductId === productId ? null : productId
    const showLookProducts = selectedProductId === productId
    setShowLook(showLookProducts)
    changeSelectedProductId(selectedProduct)
  }

  const handleProductsViewToggle = () => {
    toggleProductsView(!showProductsView)

    if (!showProductsView) {
      const productList = allProducts.map((product) => {
        return { id: product, hotspotProductType: 'main' }
      })

      quickviewTracking('shopallproducts', productList, heading)
    } else {
      quickviewTracking('quickviewClosed')
    }
  }

  return (
    <>
      <Row gutterValue={0} row='xs' data-test='m038-media-container'>
        <Styled.Wrapper data-test={dataTest} xs={12} md={6} isEditorial={isEditorial}>
          <Image
            imageFormat={imageFormat}
            src={image}
            templates={imageTemplates}
            quality={quality}
            alt={altText}
            maxWidth={width}
            responsive
            data-test={`${dataTest}-image`}
            smart
          />
          {hotSpots.map((hotSpot, index) => {
            const { points } = hotSpot
            const productID = getProductId(hotSpot, isEditorial)
            return (
              <Styled.Point
                key={`x${points.x}-y(${points.y}`}
                onClick={() => {
                  handleHotSpotClick(productID)
                }}
                data-test={`${dataTest}-point-${index}`}
                points={points}
                tabIndex={0}
                isActive={selectedProductId === productID}
              >
                <Styled.Icon width={iconSize} height={iconSize} />
              </Styled.Point>
            )
          })}
        </Styled.Wrapper>
        <Styled.ProductsWrapper xs={12} md={6} isEditorial={isEditorial} containsImage={containsImage}>
          {isEditorial ? (
            <Editorial
              items={items}
              showItems={showLook}
              selectedProductHeading={selectedProductId}
              setShowLook={setShowLook}
              changeSelectedProductId={changeSelectedProductId}
            />
          ) : (
            <>
              <Recommendations
                productSKU={selectedProductId}
                allProducts={allProducts}
                showLook={showLook}
                heading={heading}
              />
              <Button onClick={handleProductsViewToggle} data-test='m038-hotspot-button' full>
                {shopAllText}
              </Button>
            </>
          )}
        </Styled.ProductsWrapper>
      </Row>
      {showProductsView && (
        <AllProductsView
          show={showProductsView}
          onClose={handleProductsViewToggle}
          imageSrc={image}
          partNumbers={allProducts}
          data-test='m038-hotspot-all-products-view'
          altText={altText}
          heading={heading}
        />
      )}
    </>
  )
}

HotSpot.displayName = 'HotSpot'

HotSpot.propTypes = {
  /**
   * [REQUIRED] The main image for the hotspots to appear on
   */
  image: PropTypes.string.isRequired,
  /** Optional image format, eg. avif */
  imageFormat: PropTypes.string,
  /**
   * [REQUIRED] The main image alt text for the hotspots to appear on
   */
  altText: PropTypes.string.isRequired,
  /**
   * Array of objects provided by Amplience metadata for hotspot location and click targets
   */
  hotSpots: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      points: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
      selector: PropTypes.string,
      target: PropTypes.string,
      type: PropTypes.string
    })
  ).isRequired,
  /**
   * Object with xy height and width provided by Amplience as the POI for the main image
   */
  pointOfInterest: PropTypes.shape({
    h: PropTypes.number,
    w: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number
  }).isRequired,

  /**
   * Width of the main image
   */
  width: PropTypes.number,
  /**
   * Aspect ratio of the main image
   */
  aspect: PropTypes.string,
  /**
   * Quality of the main image
   */
  quality: PropTypes.number
}

HotSpot.defaultProps = {
  className: '',
  image: '',
  altText: '',
  hotSpots: [],
  pointOfInterest: {},
  width: 580,
  aspect: '1:1',
  quality: 75,
  'data-test': 'component-hotspot'
}

export default HotSpot
export { getProductId }
